// src/components/ai/PricePrediction.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Info,
  Close,
  ExpandMore,
  ExpandLess,
  AutoAwesome,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import api from '../../services/api';
import aiService from '../../services/ai';

const PricePrediction = ({ solutionId, onClose }) => {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPrediction();
  }, [solutionId]);

  const fetchPrediction = async () => {
    try {
      setLoading(true);
      const data = await aiService.getPricePrediction(solutionId);
      setPrediction(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ fontSize: 20, color: '#2E7D32' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Analyzing market data...
          </Typography>
          <LinearProgress sx={{ flex: 1, ml: 2, height: 4, borderRadius: 2 }} />
        </Box>
      </Paper>
    );
  }

  if (error || !prediction?.predicted_price) {
    return null;
  }

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return '#10B981';
    if (confidence >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const getTrendIcon = (impact) => {
    return impact === 'positive' ? 
      <TrendingUp sx={{ fontSize: 14, color: '#10B981' }} /> : 
      <TrendingDown sx={{ fontSize: 14, color: '#EF4444' }} />;
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        borderRadius: 2,
        background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
        border: '1px solid #E5E7EB',
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <AutoAwesome sx={{ fontSize: 20, color: '#2E7D32' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2E7D32' }}>
          AI Price Prediction
        </Typography>
        <Tooltip title="Based on market trends and similar solutions">
          <Info sx={{ fontSize: 14, color: '#9CA3AF' }} />
        </Tooltip>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Recommended Price
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#2E7D32' }}>
            ${prediction.predicted_price}
            <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
              /month
            </Typography>
          </Typography>
        </Box>
        <Chip
          label={`${prediction.confidence}% confidence`}
          size="small"
          sx={{ 
            bgcolor: `${getConfidenceColor(prediction.confidence)}20`,
            color: getConfidenceColor(prediction.confidence),
            fontWeight: 600,
          }}
        />
      </Box>

      {prediction.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {prediction.note}
        </Typography>
      )}

      {prediction.factors && prediction.factors.length > 0 && (
        <>
          <Button
            size="small"
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            sx={{ mt: 1, textTransform: 'none', color: '#6B7280' }}
          >
            {expanded ? 'Hide' : 'Show'} factors
          </Button>

          <Collapse in={expanded}>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              {prediction.factors.map((factor, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTrendIcon(factor.impact)}
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {factor.factor}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {factor.impact === 'positive' ? 'Increases value' : 'Decreases value'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default PricePrediction;